import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiPut, apiDelete, formatMoney, t } from "../api_helper"
import { icon } from "../lib/icons"
import { networkStatus } from "../lib/network_status"
import { isOfflineError, showOfflinePlaceholder, alertOffline } from "../lib/offline_ui"
import "../lib/paywall"

export default class extends Controller {
  static targets = ["budgetList", "inviteCode", "createModal", "newBudgetName", "renameModal", "renameBudgetName"]

  connect() {
    this.budgets = []
    this.activeId = null
    this.load()
  }

  async load() {
    try {
      const data = await apiGet("/api/shared_budgets")
      this.budgets = data.budgets
      this.activeId = data.active_id
      this.render()
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.hasBudgetListTarget ? this.budgetListTarget : null, () => this.load()); return }
      if (e.status === 403) window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "shared" } }))
      else console.error("Shared budgets load error:", e)
    }
  }

  render() {
    if (!this.hasBudgetListTarget) return

    if (!this.budgets.length) {
      this.budgetListTarget.innerHTML = `
        <div class="text-center py-8 px-4 rounded-2xl" style="background-color: rgb(248,250,252);">
          <div class="mb-2">${icon("users", "w-10 h-10 text-slate-300")}</div>
          <div class="text-sm" style="color: rgb(148,163,184);">${t("shared.no_budgets")}</div>
        </div>`
      return
    }

    this.budgetListTarget.innerHTML = this.budgets.map(b => this.budgetCard(b)).join("")
  }

  budgetCard(b) {
    const isActive = b.id === this.activeId
    const border = isActive ? "border: 1.5px solid rgb(99,102,241);" : "border: 1.5px solid transparent;"

    const membersHtml = b.members.map(m =>
      `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs"
             style="background-color: rgba(99,102,241,0.12); color: rgb(99,102,241);">
        ${m.name}
      </span>`
    ).join(" ")

    const activeBadge = isActive
      ? `<span class="text-xs px-2 py-0.5 rounded-full font-medium" style="background-color: rgb(99,102,241); color: white;">${t("shared.active_badge")}</span>`
      : ""

    const personalBadge = b.personal
      ? `<span class="text-xs px-2 py-0.5 rounded-full" style="background-color: rgba(128,128,128,0.1); color: rgb(148,163,184);">${t("shared.personal_badge")}</span>`
      : ""

    const switchBtn = !isActive
      ? `<button data-action="click->pwa-shared#switchBudget" data-budget-id="${b.id}"
                 class="flex-1 py-2 rounded-xl text-xs font-semibold active:scale-[0.98] transition-transform"
                 style="background-color: rgb(99,102,241); color: white;">
           ${t("shared.switch")}
         </button>`
      : ""

    const renameBtn = b.is_owner
      ? `<button data-action="click->pwa-shared#renameBudget" data-budget-id="${b.id}" data-budget-name="${b.name}"
                 class="active:scale-[0.98] transition-transform" style="color: rgb(148,163,184); font-size: 14px;">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>`
      : ""

    const leaveBtn = !b.is_owner
      ? `<button data-action="click->pwa-shared#leaveBudget" data-budget-id="${b.id}"
                 class="flex-1 py-2 rounded-xl text-xs font-medium active:scale-[0.98] transition-transform"
                 style="background-color: rgba(239,68,68,0.1); color: rgb(239,68,68);">
           ${t("shared.leave")}
         </button>`
      : ""

    const deleteBtn = b.is_owner && !b.personal
      ? `<button data-action="click->pwa-shared#deleteBudget" data-budget-id="${b.id}"
                 class="flex-1 py-2 rounded-xl text-xs font-medium active:scale-[0.98] transition-transform"
                 style="background-color: rgba(239,68,68,0.1); color: rgb(239,68,68);">
           ${t("shared.delete_budget")}
         </button>`
      : ""

    const codeSection = isActive ? `
      <div class="flex items-center justify-between p-3 rounded-xl mt-3" style="background-color: rgb(241,245,249);">
        <div>
          <div class="text-xs" style="color: rgb(148,163,184);">${t("shared.code_label")}</div>
          <code class="text-sm font-mono font-bold">${b.invite_code}</code>
        </div>
        <button data-action="click->pwa-shared#copyCode" data-code="${b.invite_code}"
                class="px-3 py-1.5 rounded-lg text-xs font-medium active:scale-[0.98] transition-transform"
                style="background-color: rgb(99,102,241); color: white;">
          ${t("shared.copy")}
        </button>
      </div>` : ""

    const actions = [switchBtn, leaveBtn, deleteBtn].filter(Boolean)
    const actionsHtml = actions.length
      ? `<div class="flex gap-2 mt-3">${actions.join("")}</div>`
      : ""

    return `
      <div class="p-4 rounded-2xl mb-3" style="background-color: rgb(248,250,252); ${border}">
        <div class="flex justify-between items-center mb-2">
          <div class="flex items-center gap-2">
            <span class="font-semibold text-sm">${b.name}</span>
            ${renameBtn}
          </div>
          <div class="flex items-center gap-1.5">
            ${personalBadge}
            ${activeBadge}
          </div>
        </div>
        <div class="flex items-center gap-1.5 mb-2 flex-wrap">
          ${membersHtml}
        </div>
        <div class="flex items-center gap-3 text-xs" style="color: rgb(148,163,184);">
          <span><span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:rgb(16,185,129);margin-right:4px;vertical-align:middle;"></span>${t("shared.income")}: <span style="color: rgb(16,185,129); font-weight: 600;">${formatMoney(b.total_income, b.currency)}</span></span>
          <span><span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:rgb(239,68,68);margin-right:4px;vertical-align:middle;"></span>${t("shared.expenses")}: <span style="color: rgb(239,68,68); font-weight: 600;">${formatMoney(b.total_expenses, b.currency)}</span></span>
        </div>
        ${codeSection}
        ${actionsHtml}
      </div>`
  }

  async switchBudget(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const budgetId = event.currentTarget.dataset.budgetId
    try {
      await apiPut(`/api/shared_budgets/${budgetId}/switch`)
      this.load()
    } catch (e) {
      console.error("Switch error:", e)
    }
  }

  createBudget() {
    if (this.hasCreateModalTarget) {
      if (this.hasNewBudgetNameTarget) this.newBudgetNameTarget.value = ""
      this.createModalTarget.classList.remove("hidden")
      setTimeout(() => this.newBudgetNameTarget?.focus(), 100)
    }
  }

  hideCreateModal() {
    if (this.hasCreateModalTarget) this.createModalTarget.classList.add("hidden")
  }

  async submitCreate() {
    if (!networkStatus.online) { alertOffline(); return }
    const name = this.hasNewBudgetNameTarget ? this.newBudgetNameTarget.value.trim() : ""
    if (!name) return

    try {
      await apiPost("/api/shared_budgets", { name })
      this.hideCreateModal()
      this.load()
    } catch (e) {
      if (e.status === 403) window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "shared" } }))
      else alert(e.message || "Error")
    }
  }

  renameBudget(event) {
    this.renamingBudgetId = event.currentTarget.dataset.budgetId
    const currentName = event.currentTarget.dataset.budgetName
    if (this.hasRenameModalTarget) {
      if (this.hasRenameBudgetNameTarget) this.renameBudgetNameTarget.value = currentName
      this.renameModalTarget.classList.remove("hidden")
      setTimeout(() => this.renameBudgetNameTarget?.focus(), 100)
    }
  }

  hideRenameModal() {
    if (this.hasRenameModalTarget) this.renameModalTarget.classList.add("hidden")
    this.renamingBudgetId = null
  }

  async submitRename() {
    if (!networkStatus.online) { alertOffline(); return }
    if (!this.renamingBudgetId) return
    const name = this.hasRenameBudgetNameTarget ? this.renameBudgetNameTarget.value.trim() : ""
    if (!name) return

    try {
      await apiPut(`/api/shared_budgets/${this.renamingBudgetId}/rename`, { name })
      this.hideRenameModal()
      this.load()
    } catch (e) {
      console.error("Rename error:", e)
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  copyCode(event) {
    const code = event.currentTarget.dataset.code
    navigator.clipboard?.writeText(code)
    const btn = event.currentTarget
    const original = btn.textContent
    btn.innerHTML = icon("check", "w-4 h-4 inline text-emerald-500")
    setTimeout(() => { btn.textContent = original }, 1500)
  }

  async joinBudget(event) {
    event.preventDefault()
    if (!networkStatus.online) { alertOffline(); return }
    try {
      await apiPost("/api/shared_budgets/join", {
        invite_code: this.inviteCodeTarget.value
      })
      this.inviteCodeTarget.value = ""
      this.load()
    } catch (e) {
      if (e.status === 403) window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "shared" } }))
      else alert(e.status === 403 ? e.message : (t("shared.invalid_code") || "Invalid invite code"))
    }
  }

  async leaveBudget(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const budgetId = event.currentTarget.dataset.budgetId
    if (!confirm(t("shared.leave_confirm") || "Leave this shared budget?")) return

    try {
      await apiDelete(`/api/shared_budgets/${budgetId}/leave`)
      this.load()
    } catch (e) {
      console.error("Leave error:", e)
    }
  }

  async deleteBudget(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const budgetId = event.currentTarget.dataset.budgetId
    if (!confirm(t("shared.delete_budget_confirm") || "Delete this budget?")) return

    try {
      await apiDelete(`/api/shared_budgets/${budgetId}`)
      this.load()
    } catch (e) {
      alert(e.message || "Error")
    }
  }
}
